import { useEffect, useRef } from 'react';
import { Userpilot } from 'userpilot';

import { useAuth } from '../providers/AuthProvider';
import { useLocale } from '../providers/LocalizationProvider';

/**
 * Identifies the user in Userpilot once when they become authenticated.
 * Will not re-identify if the user is impersonated.
 */
export function useIdentifyUserInUserpilot(): void {
    const { isAuthenticated, isImpersonated, userId, username } = useAuth();
    const { locale } = useLocale();
    const identifiedUserId = useRef<string | number | null>(null);

    useEffect(() => {
        if (!isAuthenticated) {
            identifiedUserId.current = null;
            return;
        }

        // Only identify if:
        // - User is authenticated
        // - User is not impersonated
        // - We haven't identified this specific user yet
        // - We have valid userId and username
        if (
            isAuthenticated &&
            !isImpersonated &&
            identifiedUserId.current !== userId &&
            userId &&
            username
        ) {
            try {
                Userpilot.identify(userId.toString(), {
                    name: username,
                    email: username,
                    created_at: new Date(),
                    locale_code: locale,
                });
                identifiedUserId.current = userId;
            } catch (error) {
                console.error('Failed to identify user in Userpilot:', error);
            }
        }
    }, [isAuthenticated, isImpersonated, userId, username, locale]);
}
